/**
 * Rich content utilities for the FE (learner) app.
 * Mirrors admin_airsmart's `utils/richContent.js` so both clients normalize the
 * same shape returned by BE.
 *
 * Canonical shape: { delta: { ops: [...] }, html: string, plainText: string }
 * Also accepts a legacy plain string from older DB rows.
 *
 * Wasabi URL signing: DB stores canonical (unsigned) URLs; render layer signs
 * them so private bucket fetches succeed for the brief expiry window.
 */
import { convertToSignedUrl } from "./wasabiHelper";

const escapeHtml = (s) =>
  String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const stripHtml = (html) =>
  String(html)
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const deltaToPlainText = (delta) => {
  if (!delta || !Array.isArray(delta.ops)) return "";
  return delta.ops
    .map((op) => (typeof op.insert === "string" ? op.insert : ""))
    .join("")
    .replace(/\n+$/, "");
};

/**
 * In Quill delta, a code-block is represented as:
 *   { insert: "line text" }  followed by  { insert: "\n", attributes: { "code-block": "plain"|true } }
 * The \n op is the LINE TERMINATOR for the preceding text segment.
 * indent on the \n op means that line should be indented (1 indent = 4 spaces).
 *
 * Security:
 *   - indent is clamped to [0, MAX_INDENT] to prevent memory exhaustion via repeat()
 *   - pendingText is clamped to MAX_LINE_LENGTH to prevent memory exhaustion
 *   - output is plain text only; HTML encoding is done by the caller before DOMPurify
 */
const MAX_INDENT =
  parseInt(process.env.REACT_APP_CODE_BLOCK_MAX_INDENT, 10) || 32;
const MAX_LINE_LENGTH =
  parseInt(process.env.REACT_APP_CODE_BLOCK_MAX_LINE_LENGTH, 10) ||
  10 * 1024 * 1024;

function extractCodeBlocksFromDelta(delta) {
  if (!delta || !Array.isArray(delta.ops)) return [];

  const blocks = [];
  let pendingText = "";
  let inCodeBlock = false;
  let currentBlockLines = [];

  for (const op of delta.ops) {
    if (typeof op.insert !== "string") {
      pendingText = "";
      continue;
    }

    const isNewline = op.insert === "\n";
    const isCodeBlock = op.attributes && op.attributes["code-block"];

    if (isNewline && isCodeBlock) {
      const rawIndent = Number(op.attributes.indent) || 0;
      const indent = Math.max(0, Math.min(MAX_INDENT, Math.floor(rawIndent)));
      const indentStr = "    ".repeat(indent);
      currentBlockLines.push(indentStr + pendingText);
      pendingText = "";
      inCodeBlock = true;
    } else if (isNewline && !isCodeBlock) {
      if (inCodeBlock && currentBlockLines.length > 0) {
        blocks.push(currentBlockLines.join("\n"));
        currentBlockLines = [];
        inCodeBlock = false;
      }
      pendingText = "";
    } else {
      const parts = op.insert.split("\n");
      if (parts.length === 1) {
        const next = pendingText + op.insert;
        pendingText =
          next.length > MAX_LINE_LENGTH ? next.slice(0, MAX_LINE_LENGTH) : next;
      } else {
        if (inCodeBlock && currentBlockLines.length > 0) {
          blocks.push(currentBlockLines.join("\n"));
          currentBlockLines = [];
          inCodeBlock = false;
        }
        const last = parts[parts.length - 1];
        pendingText =
          last.length > MAX_LINE_LENGTH ? last.slice(0, MAX_LINE_LENGTH) : last;
      }
    }
  }

  if (inCodeBlock && currentBlockLines.length > 0) {
    blocks.push(currentBlockLines.join("\n"));
  }

  return blocks;
}

/**
 * Replace <pre> innerHTML in `html` with accurate text from delta code blocks.
 * The replacement text is HTML-encoded BEFORE being injected, then the whole
 * string goes through DOMPurify — double defence against XSS.
 * `attrs` captured from the existing <pre> tag is intentionally DROPPED and
 * replaced with only the safe data-language attribute (if present) to prevent
 * attribute injection from a compromised HTML field.
 */
export function patchCodeBlocksFromDelta(html, delta) {
  const codeBlocks = extractCodeBlocksFromDelta(delta);
  if (!codeBlocks.length) return html;

  let blockIdx = 0;
  return html.replace(/<pre([^>]*)>[\s\S]*?<\/pre>/gi, (match, rawAttrs) => {
    if (blockIdx >= codeBlocks.length) return match;
    const text = codeBlocks[blockIdx++];

    const langMatch =
      /data-language\s*=\s*"([^"]*)"/.exec(rawAttrs) ||
      /data-language\s*=\s*'([^']*)'/.exec(rawAttrs);
    const safeAttrs = langMatch
      ? ` data-language="${langMatch[1].replace(/[^a-zA-Z0-9._+-]/g, "")}"`
      : "";

    const encoded = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    return `<pre${safeAttrs}>${encoded}</pre>`;
  });
}

export function normalizeRichContent(value) {
  if (value == null) return { delta: { ops: [] }, html: "", plainText: "" };

  if (typeof value === "string") {
    const trimmed = value;
    return {
      delta: {
        ops: trimmed
          ? [{ insert: trimmed.endsWith("\n") ? trimmed : trimmed + "\n" }]
          : [],
      },
      html: trimmed
        ? `<p>${escapeHtml(trimmed).replace(/\n/g, "</p><p>")}</p>`
        : "",
      plainText: trimmed,
    };
  }

  if (typeof value === "object") {
    const hasDelta = value.delta && Array.isArray(value.delta.ops);
    const hasHtml = typeof value.html === "string";
    const hasPlain = typeof value.plainText === "string";

    if (hasDelta && hasHtml && hasPlain) {
      return {
        delta: { ops: value.delta.ops },
        html: value.html,
        plainText: value.plainText,
      };
    }
    if (hasDelta) {
      const plain = deltaToPlainText(value.delta);
      return {
        delta: { ops: value.delta.ops },
        html: hasHtml ? value.html : "",
        plainText: hasPlain ? value.plainText : plain,
      };
    }
    if (hasHtml) {
      const plain = hasPlain ? value.plainText : stripHtml(value.html);
      return {
        delta: { ops: plain ? [{ insert: plain + "\n" }] : [] },
        html: value.html,
        plainText: plain,
      };
    }
    if (Array.isArray(value.ops)) {
      const plain = deltaToPlainText(value);
      return { delta: { ops: value.ops }, html: "", plainText: plain };
    }
  }

  return { delta: { ops: [] }, html: "", plainText: "" };
}

// Custom-page embeds (airsmartVideo / airsmartModel3d) count as non-empty
// content even when the plain text is blank — an embed-only page is valid.
export const isRichContentEmpty = (rc) => {
  const n = normalizeRichContent(rc);
  if (n.plainText.trim()) return false;
  const hasEmbed = (n.delta?.ops || []).some(
    (op) =>
      op &&
      op.insert &&
      typeof op.insert === "object" &&
      (op.insert.image || op.insert.airsmartVideo || op.insert.airsmartModel3d),
  );
  if (hasEmbed) return false;
  return !n.html.replace(/<[^>]+>/g, "").trim();
};

// --- Wasabi URL signing (render-only) ---------------------------------------

const isWasabiUrl = (url) =>
  typeof url === "string" && url.includes("wasabisys.com");

const isAlreadySigned = (url) =>
  typeof url === "string" &&
  /[?&](AWSAccessKeyId|X-Amz-Signature)=/.test(url);

const signUrl = (url, expiresIn = 3600) => {
  if (!isWasabiUrl(url) || isAlreadySigned(url)) return url;
  const signed = convertToSignedUrl(url, expiresIn);
  return signed || url;
};

export const signImagesInHtml = (html) => {
  if (!html || typeof html !== "string") return html;
  return html.replace(
    /<img\b([^>]*?)\ssrc\s*=\s*(["'])([^"']+)\2/gi,
    (_match, attrs, quote, src) =>
      `<img${attrs} src=${quote}${signUrl(src)}${quote}`,
  );
};
